import { type FC, type ReactNode } from 'react';

import { ExperimentProvider } from '@eneco/experiments';
import { FeaturesReady, useFeatureValue } from '@growthbook/growthbook-react';

/**
 * GrowthBook flag for showing redelivery split into the last netted year and the first year without netting.
 *
 * Deliberately no date in code: the switch-over (15 December 2026, when every new contract starts in 2027 and
 * no netted period is left to show) is a scheduled rule on this feature in GrowthBook. Once it is off, the
 * price breakdown shows a single redelivery section on DC's current prices, without year labels.
 *
 * On unless GrowthBook explicitly says off. While DC still splits the year, its current-period refund covers
 * only the netted year, so the single section would multiply the full remainder by the rate yet show only that
 * year's amount; the split is the only correct view until the switch-over turns it off.
 */
export const NETTING_SPLIT_FEATURE_ID = '888300|sme-netting-split';

interface Props {
  children: (isNettingSplitEnabled: boolean) => ReactNode;
}

const IS_NETTING_SPLIT_ENABLED_BY_DEFAULT = true;

const NettingSplitFlag: FC<Props> = ({ children }) => (
  <>{children(useFeatureValue(NETTING_SPLIT_FEATURE_ID, IS_NETTING_SPLIT_ENABLED_BY_DEFAULT))}</>
);

/**
 * Resolves the flag and hands it to `children`. While GrowthBook loads, when it cannot be reached, or when the
 * feature does not exist there, the flag reads as on, so the split shows; only an explicit "off" hides it.
 */
export const NettingSplitFeature: FC<Props> = ({ children }) => (
  <ExperimentProvider featureId={NETTING_SPLIT_FEATURE_ID}>
    <FeaturesReady fallback={children(IS_NETTING_SPLIT_ENABLED_BY_DEFAULT)} timeout={1000}>
      <NettingSplitFlag>{children}</NettingSplitFlag>
    </FeaturesReady>
  </ExperimentProvider>
);
